/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Helper function to normalize role values
  const normalizeRole = (role) => {
    if (!role) return null;
    const normalized = role.toLowerCase().trim();
    
    // Exact matches first (most specific)
    if (normalized === 'employer') return 'employer';
    if (normalized === 'recruiter') return 'recruiter';
    if (normalized === 'jobseeker' || normalized === 'job_seeker') return 'jobSeeker';
    if (normalized === 'intern') return 'intern';
    if (normalized === 'admin' || normalized === 'administrator') return 'admin';
    if (normalized === 'university') return 'university';
    if (normalized === 'institute') return 'institute';
    if (normalized === 'association') return 'association';
    if (normalized === 'ngo') return 'ngo';
    
    // Then check for partial matches (less specific)
    const normalizedNoUnderscore = normalized.replace(/[_-]/g, '');
    if (normalizedNoUnderscore.includes('recruiter')) return 'recruiter';
    if (normalizedNoUnderscore.includes('employer')) return 'employer';
    if (normalizedNoUnderscore.includes('jobseeker')) return 'jobSeeker';
    if (normalizedNoUnderscore.includes('intern')) return 'intern';
    if (normalizedNoUnderscore.includes('admin')) return 'admin';
    if (normalizedNoUnderscore.includes('university') || normalizedNoUnderscore.includes('campus')) return 'university';
    if (normalizedNoUnderscore.includes('institute')) return 'institute';
    if (normalizedNoUnderscore.includes('association') || normalizedNoUnderscore.includes('assoc')) return 'association';
    if (normalizedNoUnderscore.includes('ngo')) return 'ngo';
    
    return role; // Return original if no match
  };

  useEffect(() => {
    console.log('🔐 AuthContext useEffect triggered - checking localStorage');
    
    // Clean up any 'undefined'/'NULL' values in localStorage
    const cleanupLocalStorage = () => {
      const keys = ['userEmail', 'userFirstName', 'userLastName', 'email', 'firstName', 'lastName', 'phone', 'userPhone'];
      keys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value === 'undefined' || value === 'NULL' || value === 'null') {
          localStorage.removeItem(key);
        }
      });
    };
    
    cleanupLocalStorage();
    
    // Check for existing authentication on app load
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const userId = localStorage.getItem('userId');
    const email = localStorage.getItem('userEmail') || localStorage.getItem('email');
    const firstName = localStorage.getItem('userFirstName') || localStorage.getItem('firstName');
    const lastName = localStorage.getItem('userLastName') || localStorage.getItem('lastName');
    const phone = localStorage.getItem('userPhone') || localStorage.getItem('phone');
    const userType = localStorage.getItem('userType');
    const profileCompleted = localStorage.getItem('profileCompleted') === 'true';
    const hasCompletedProfile = localStorage.getItem('hasCompletedProfile') === 'true';

    console.log('🔐 AuthContext - localStorage values:', {
      token: token ? 'EXISTS' : 'NULL',
      role: role || 'NULL',
      userId: userId || 'NULL',
      email: email || 'NULL',
      firstName: firstName || 'NULL',
      lastName: lastName || 'NULL',
      phone: phone || 'NULL',
      userType: userType || 'NULL',
      profileCompleted: profileCompleted,
      hasCompletedProfile: hasCompletedProfile
    });

    if (token && role && userId) {
      console.log('🔐 AuthContext - Valid tokens found, logging in user');
      const normalizedRole = normalizeRole(role);
      // Only set valid user data (not 'undefined'/'NULL' strings)
      const isBad = (v) => !v || v === 'undefined' || v === 'NULL' || v === 'null';
      const validEmail = isBad(email) ? '' : email;
      const validFirstName = isBad(firstName) ? '' : firstName;
      const validLastName = isBad(lastName) ? '' : lastName;
      const validPhone = isBad(phone) ? '' : phone;
      
      setUser({
        token,
        role: normalizedRole,
        userId,
        email: validEmail,
        firstName: validFirstName,
        lastName: validLastName,
        phone: validPhone,
        userType: userType || normalizedRole,
        profileCompleted: profileCompleted,
        hasCompletedProfile: hasCompletedProfile
      });
      setIsAuthenticated(true);
      console.log('🔐 AuthContext - User logged in:', { role: normalizedRole, userId });
      
      // Don't redirect on page refresh - let the user stay on their current page
      console.log('🔐 AuthContext - User authenticated, staying on current page');
    } else {
      console.log('🔐 AuthContext - No valid tokens found, user not authenticated');
      // Ensure user state is cleared and isAuthenticated is false
      setUser(null);
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, [navigate]);

  const login = async (email, password, shouldNavigate = true, intendedDestination = null) => {
    console.log('🔐 AuthContext login() called with:', { email, password, shouldNavigate, intendedDestination });
    
    try {
      const response = await fetch(`${buildApiUrl('/api/auth/login')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.message || errorData.error || `Login failed (${response.status})`);
        } else {
          const errorText = await response.text();
          console.error('🔐 AuthContext - Non-JSON error response:', errorText?.slice(0, 200));
          throw new Error(`Login failed (${response.status})`);
        }
      }

      const userData = await response.json();
      console.log('🔐 AuthContext - Login successful:', userData);
      
      // Continue with existing login logic
      const { token, role, userId, firstName, lastName, phone, ...otherData } = userData;
      console.log('🔐 AuthContext - Raw role from backend:', role);
      console.log('🔐 AuthContext - User data role:', userData.role);
      console.log('🔐 AuthContext - User data userType:', userData.user?.userType);
      const normalizedRole = normalizeRole(role);
      console.log('🔐 AuthContext - Normalized role:', normalizedRole);
      
      // Validate and clean user data before storing
      const isBad = (v) => !v || v === 'undefined' || v === 'NULL' || v === 'null';
      const validEmail = isBad(email) ? '' : email;
      const validFirstName = isBad(firstName) ? '' : firstName;
      const validLastName = isBad(lastName) ? '' : lastName;
      const validPhone = isBad(phone) ? '' : phone;
      
      localStorage.setItem('token', token);
      localStorage.setItem('role', normalizedRole);
      localStorage.setItem('userId', userId);
      localStorage.setItem('userEmail', validEmail);
      localStorage.setItem('userFirstName', validFirstName);
      localStorage.setItem('userLastName', validLastName);
      localStorage.setItem('userPhone', validPhone);
      localStorage.setItem('email', validEmail);
      localStorage.setItem('firstName', validFirstName);
      localStorage.setItem('lastName', validLastName);
      localStorage.setItem('phone', validPhone);
      if (otherData.profileCompleted !== undefined) {
        localStorage.setItem('profileCompleted', String(otherData.profileCompleted));
      }
      if (otherData.hasCompletedProfile !== undefined) {
        localStorage.setItem('hasCompletedProfile', String(otherData.hasCompletedProfile));
      }
      // Store userType if available
      if (otherData.userType) {
        localStorage.setItem('userType', otherData.userType);
      }
      
      setUser({ token, role: normalizedRole, userId, email: validEmail, firstName: validFirstName, lastName: validLastName, phone: validPhone, ...otherData });
      setIsAuthenticated(true);
      
      console.log('🔐 AuthContext - User logged in via login() function:', { 
        role: normalizedRole, 
        userId, 
        shouldNavigate, 
        intendedDestination, 
        profileCompleted: otherData.profileCompleted,
        bulkImported: otherData.bulkImported,
        allData: otherData
      });
      
      // Only redirect if shouldNavigate is true (default behavior)
      if (shouldNavigate) {
        // Check for intended destination first (from ProtectedRoute)
        const stored = sessionStorage.getItem('intendedDestination');
        let targetPath = intendedDestination;
        let targetState = null;
        if (!targetPath && stored) {
          try {
            const parsed = JSON.parse(stored);
            if (parsed && typeof parsed === 'object' && parsed.pathname) {
              targetPath = parsed.pathname;
              targetState = parsed.state || null;
            } else if (typeof stored === 'string') {
              targetPath = stored;
            }
          } catch {
            targetPath = stored;
          }
        }
        
        if (targetPath && targetPath !== '/login') {
          console.log('🔐 AuthContext - Redirecting to intended destination:', targetPath);
          sessionStorage.removeItem('intendedDestination'); // Clean up
          navigate(targetPath, targetState ? { state: targetState } : {});
          return;
        }
        
        // Default dashboard redirect - check profile completion first
        console.log('🔐 AuthContext - Navigating to dashboard for role:', normalizedRole);
        console.log('🔐 AuthContext - Profile completed status:', otherData.profileCompleted);
        
        if (normalizedRole === 'admin') {
          navigate('/admin');
        } else if (normalizedRole === 'recruiter') {
          // Check if recruiter has completed profile
          console.log('🔐 AuthContext - Recruiter login profile check:', {
            profileCompleted: otherData.profileCompleted,
            hasCompletedProfile: otherData.hasCompletedProfile,
            bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile
          });
          if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
            console.log('🔐 AuthContext - Recruiter profile incomplete, redirecting to registration form');
            navigate('/recruiter-registration');
          } else {
            console.log('🔐 AuthContext - Recruiter profile complete, redirecting to dashboard');
            navigate('/recruiter-dashboard');
          }
        } else if (normalizedRole === 'employer') {
          // Check if employer has completed profile
          console.log('🔐 AuthContext - Employer login profile check:', {
            profileCompleted: otherData.profileCompleted,
            hasCompletedProfile: otherData.hasCompletedProfile,
            bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile
          });
          console.log('🔐 AuthContext - CONFIRMED: Normalized role is "employer", routing to employer dashboard');
          if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
            console.log('🔐 AuthContext - Employer profile incomplete, redirecting to registration');
            navigate('/employer-registration');
          } else {
            console.log('🔐 AuthContext - Employer profile complete, redirecting to EMPLOYER dashboard');
            navigate('/employer-dashboard');
          }
        } else if (normalizedRole === 'jobSeeker') {
          // Check if job seeker has completed profile
          console.log('🔐 AuthContext - Job seeker login profile check:', {
            profileCompleted: otherData.profileCompleted,
            hasCompletedProfile: otherData.hasCompletedProfile,
            bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile
          });
          if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
            console.log('🔐 AuthContext - Job seeker profile incomplete, redirecting to setup choice');
            navigate('/setup-choice');
          } else {
            console.log('🔐 AuthContext - Job seeker profile complete, redirecting to dashboard');
            navigate('/jobseeker-dashboard');
          }
        } else if (normalizedRole === 'intern') {
          // Check if intern has completed profile
          console.log('🔐 AuthContext - Intern login profile check:', {
            profileCompleted: otherData.profileCompleted,
            hasCompletedProfile: otherData.hasCompletedProfile,
            bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile
          });
          if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
            console.log('🔐 AuthContext - Intern profile incomplete, redirecting to setup choice');
            navigate('/setup-choice');
          } else {
            console.log('🔐 AuthContext - Intern profile complete, redirecting to dashboard');
            navigate('/intern-dashboard');
          }
        } else if (normalizedRole === 'university') {
          // Check if university has completed profile
          console.log('🔐 AuthContext - University login profile check:', {
            profileCompleted: otherData.profileCompleted,
            hasCompletedProfile: otherData.hasCompletedProfile,
            bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile,
            universityName: otherData.universityName || userData.user?.universityName
          });
          
          // Check if this is Moringa School - redirect to Moringa Dashboard
          const universityName = (otherData.universityName || userData.user?.universityName || '').toLowerCase();
          const isMoringa = universityName.includes('moringa');
          
          if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
            console.log('🔐 AuthContext - University profile incomplete, redirecting to registration');
            navigate('/university-registration');
          } else {
            if (isMoringa) {
              console.log('🔐 AuthContext - Moringa School detected, redirecting to Moringa Dashboard');
              navigate('/moringa-dashboard');
            } else {
              console.log('🔐 AuthContext - University profile complete, redirecting to university dashboard');
              navigate('/university-dashboard');
            }
          }
        } else if (normalizedRole === 'institute') {
          // Check if institute has completed profile
          console.log('🔐 AuthContext - Institute login profile check:', {
            profileCompleted: otherData.profileCompleted,
            hasCompletedProfile: otherData.hasCompletedProfile,
            bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile
          });
          if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
            console.log('🔐 AuthContext - Institute profile incomplete, redirecting to registration');
            navigate('/institute-registration');
          } else {
            console.log('🔐 AuthContext - Institute profile complete, redirecting to dashboard');
            navigate('/institute-dashboard');
          }
        } else if (normalizedRole === 'association') {
          // Association users – check profile completion
          console.log('🔐 AuthContext - Association login profile check:', {
            profileCompleted: otherData.profileCompleted,
            hasCompletedProfile: otherData.hasCompletedProfile,
            bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile
          });
          if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
            console.log('🔐 AuthContext - Association profile incomplete, redirecting to registration');
            navigate('/association-registration');
          } else {
            console.log('🔐 AuthContext - Association profile complete, redirecting to dashboard');
            navigate('/association-dashboard');
          }
        } else if (normalizedRole === 'ngo') {
          // NGO users – check profile completion
          console.log('🔐 AuthContext - NGO login profile check:', {
            profileCompleted: otherData.profileCompleted,
            hasCompletedProfile: otherData.hasCompletedProfile,
            bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile
          });
          if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
            console.log('🔐 AuthContext - NGO profile incomplete, redirecting to registration');
            navigate('/ngo-registration');
          } else {
            console.log('🔐 AuthContext - NGO profile complete, redirecting to dashboard');
            navigate('/ngo-dashboard');
          }
        } else {
          // Default to job seeker dashboard
          console.log('🔐 AuthContext - Unknown role in login(), defaulting to job seeker dashboard');
          navigate('/jobseeker-dashboard');
        }
      }
      
      return userData;
    } catch (error) {
      console.error('🔐 AuthContext - Login error:', error);
      throw error;
    }
  };

  const loginWithUserData = (userData, shouldNavigate = true, intendedDestination = null) => {
    console.log('🔐 AuthContext login() called with:', { userData, shouldNavigate, intendedDestination });
    console.trace('🔐 Login function call stack:');
    
    const { token, role, userId, email, firstName, lastName, phone, phoneNumber, ...otherData } = userData;
    const normalizedRole = normalizeRole(role);
    
    // Validate and clean user data before storing
    const validEmail = email && email !== 'undefined' ? email : '';
    const validFirstName = firstName && firstName !== 'undefined' ? firstName : '';
    const validLastName = lastName && lastName !== 'undefined' ? lastName : '';
    const validPhone = (phone && phone !== 'undefined' ? phone : '') || (phoneNumber && phoneNumber !== 'undefined' ? phoneNumber : '');
    
    localStorage.setItem('token', token);
    localStorage.setItem('role', normalizedRole);
    localStorage.setItem('userId', userId);
    localStorage.setItem('userEmail', validEmail);
    localStorage.setItem('userFirstName', validFirstName);
    localStorage.setItem('userLastName', validLastName);
    localStorage.setItem('userPhone', validPhone);
    localStorage.setItem('email', validEmail);
    localStorage.setItem('firstName', validFirstName);
    localStorage.setItem('lastName', validLastName);
    localStorage.setItem('phone', validPhone);
    
    // Store userType if available
    if (otherData.userType) {
      localStorage.setItem('userType', otherData.userType);
    }
    
    // Store profile completion status if available
    if (otherData.profileCompleted !== undefined) {
      localStorage.setItem('profileCompleted', otherData.profileCompleted.toString());
    }
    if (otherData.hasCompletedProfile !== undefined) {
      localStorage.setItem('hasCompletedProfile', otherData.hasCompletedProfile.toString());
    }
    
    setUser({ token, role: normalizedRole, userId, email: validEmail, firstName: validFirstName, lastName: validLastName, phone: validPhone, phoneNumber: validPhone, ...otherData });
    setIsAuthenticated(true);
    
    console.log('🔐 AuthContext - User logged in via login() function:', { 
      role: normalizedRole, 
      userId, 
      shouldNavigate, 
      intendedDestination, 
      profileCompleted: otherData.profileCompleted,
      bulkImported: otherData.bulkImported,
      allData: otherData
    });
    
    // Only redirect if shouldNavigate is true (default behavior)
    if (shouldNavigate) {
      // Check for intended destination first (from ProtectedRoute)
      const stored = sessionStorage.getItem('intendedDestination');
      let targetPath = intendedDestination;
      let targetState = null;
      if (!targetPath && stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed && typeof parsed === 'object' && parsed.pathname) {
            targetPath = parsed.pathname;
            targetState = parsed.state || null;
          } else if (typeof stored === 'string') {
            targetPath = stored;
          }
        } catch {
          targetPath = stored;
        }
      }
      
      if (targetPath && targetPath !== '/login') {
        console.log('🔐 AuthContext - Redirecting to intended destination:', targetPath);
        sessionStorage.removeItem('intendedDestination'); // Clean up
        navigate(targetPath, targetState ? { state: targetState } : {});
        return;
      }
      
      // Default dashboard redirect - check profile completion first
      console.log('🔐 AuthContext - Navigating to dashboard for role:', normalizedRole);
      console.log('🔐 AuthContext - Profile completed status:', otherData.profileCompleted);
      
      if (normalizedRole === 'admin') {
        navigate('/admin');
      } else if (normalizedRole === 'recruiter') {
        // Check if recruiter has completed profile
        if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
          console.log('🔐 AuthContext - Recruiter profile incomplete, redirecting to registration form');
          navigate('/recruiter-registration');
        } else {
          navigate('/recruiter-dashboard');
        }
      } else if (normalizedRole === 'employer') {
        // Check if employer has completed profile
        console.log('🔐 AuthContext - Employer profile check:', {
          profileCompleted: otherData.profileCompleted,
          hasCompletedProfile: otherData.hasCompletedProfile,
          bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile
        });
        if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
          console.log('🔐 AuthContext - Employer profile incomplete, redirecting to registration');
          navigate('/employer-registration');
        } else {
          console.log('🔐 AuthContext - Employer profile complete, redirecting to dashboard');
          navigate('/employer-dashboard');
        }
      } else if (normalizedRole === 'jobSeeker') {
        // Check if job seeker has completed profile
        console.log('🔐 AuthContext - Job seeker profile check:', {
          profileCompleted: otherData.profileCompleted,
          hasCompletedProfile: otherData.hasCompletedProfile,
          bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile
        });
        if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
          console.log('🔐 AuthContext - Job seeker profile incomplete, redirecting to setup choice');
          navigate('/setup-choice');
        } else {
          console.log('🔐 AuthContext - Job seeker profile complete, redirecting to dashboard');
          navigate('/jobseeker-dashboard');
        }
      } else if (normalizedRole === 'intern') {
        // Check if intern has completed profile
        if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
          console.log('🔐 AuthContext - Intern profile incomplete, redirecting to setup choice');
          navigate('/setup-choice');
        } else {
          console.log('🔐 AuthContext - Intern profile complete, redirecting to dashboard');
          navigate('/intern-dashboard');
        }
      } else if (normalizedRole === 'university') {
        // University users - redirect to university dashboard or profile setup
        console.log('🔐 AuthContext - University user login');
        if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
          console.log('🔐 AuthContext - University profile incomplete, redirecting to university registration');
          navigate('/university-registration');
        } else {
          console.log('🔐 AuthContext - University profile complete, redirecting to dashboard');
          navigate('/university-dashboard');
        }
      } else if (normalizedRole === 'institute') {
        // Check if institute has completed profile
        console.log('🔐 AuthContext - Institute login profile check:', {
          profileCompleted: otherData.profileCompleted,
          hasCompletedProfile: otherData.hasCompletedProfile,
          bothFalse: !otherData.profileCompleted && !otherData.hasCompletedProfile
        });
        if (!otherData.profileCompleted && !otherData.hasCompletedProfile) {
          console.log('🔐 AuthContext - Institute profile incomplete, redirecting to registration');
          navigate('/institute-registration');
        } else {
          console.log('🔐 AuthContext - Institute profile complete, redirecting to dashboard');
          navigate('/institute-dashboard');
        }
      } else if (normalizedRole === 'association') {
        // Association users - check profile completion
        const hasCompletedProfile = otherData.profileCompleted || otherData.hasCompletedProfile;
        if (!hasCompletedProfile) {
          console.log('🔐 AuthContext - Association profile incomplete, redirecting to registration');
          navigate('/association-registration');
        } else {
          console.log('🔐 AuthContext - Association profile complete, redirecting to dashboard');
          navigate('/association-dashboard');
        }
      } else if (normalizedRole === 'ngo') {
        // NGO users - check profile completion
        const hasCompletedProfile = otherData.profileCompleted || otherData.hasCompletedProfile;
        if (!hasCompletedProfile) {
          console.log('🔐 AuthContext - NGO profile incomplete, redirecting to registration');
          navigate('/ngo-registration');
        } else {
          console.log('🔐 AuthContext - NGO profile complete, redirecting to dashboard');
          navigate('/ngo-dashboard');
        }
      } else {
        // Default to job seeker dashboard
        console.log('🔐 AuthContext - Unknown role in loginWithUserData(), defaulting to job seeker dashboard');
        navigate('/jobseeker-dashboard');
      }
    }
  };

  const logout = () => {
    console.log('🔐 AuthContext - logout() called, clearing all auth data');
    
    // Clear all authentication-related localStorage items
    const authKeys = [
      'token', 'role', 'userId', 'userEmail', 'userFirstName', 'userLastName', 'userPhone',
      'email', 'firstName', 'lastName', 'phone',
      'user', 'authToken', 'userRole', 'currentUser', 'userData'
    ];
    
    authKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        console.log(`🔐 Removing localStorage key: ${key}`);
        localStorage.removeItem(key);
      }
    });
    
    // Clear any session storage that might persist state
    sessionStorage.removeItem('intendedDestination');
    
    // Clear state
    setUser(null);
    setIsAuthenticated(false);
    
    console.log('🔐 AuthContext - logout() complete, user should be logged out');
    console.log('🔐 AuthContext - isAuthenticated set to:', false);
    console.log('🔐 AuthContext - user set to:', null);
    
    // Force a page reload to ensure all components reset their state
    window.location.href = '/';
  };

  // Force clear all authentication data (for debugging)
  const forceLogout = () => {
    console.log('🔐 Force logout - clearing all localStorage data');
    
    // Clear all possible auth-related localStorage keys
    const authKeys = [
      'token', 'role', 'userId', 'userEmail', 'userFirstName', 'userLastName',
      'authToken', 'user', 'userRole', 'currentUser', 'userData'
    ];
    
    authKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        console.log(`🔐 Removing localStorage key: ${key}`);
        localStorage.removeItem(key);
      }
    });
    
    setUser(null);
    setIsAuthenticated(false);
    console.log('🔐 Force logout complete - user should now see login/signup');
    navigate('/');
  };

  const signup = async (userData) => {
    console.log('🔐 AuthContext signup() called with:', userData);
    
    try {
      const response = await fetch(`${buildApiUrl('/api/auth/signup')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      // Check if response is JSON or HTML
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      if (!response.ok) {
        if (isJson) {
          const errorData = await response.json();
          // If there are password errors, create a detailed error message
          if (errorData.passwordErrors && Array.isArray(errorData.passwordErrors)) {
            const errorMessage = errorData.passwordErrors.join('\n• ');
            throw new Error(`Password requirements not met:\n• ${errorMessage}`);
          }
          throw new Error(errorData.error || errorData.message || 'Signup failed');
        } else {
          // Backend returned HTML error page instead of JSON
          const errorText = await response.text();
          console.error('🔐 Backend returned non-JSON response:', errorText.substring(0, 200));
          throw new Error('Server error occurred. Please try again later or contact support.');
        }
      }

      if (!isJson) {
        throw new Error('Invalid server response. Please try again later.');
      }

      const data = await response.json();
      console.log('🔐 AuthContext - Signup successful:', data);
      console.log('🔐 AuthContext - Signup profileCompleted:', data.profileCompleted);
      console.log('🔐 AuthContext - Signup hasCompletedProfile:', data.hasCompletedProfile);
      
      // Auto-login after successful signup
      if (data.token) {
        console.log('🔐 AuthContext - Auto-login after signup, calling loginWithUserData');
        console.log('🔐 AuthContext - Signup response data:', { role: data.role, userType: data.userType, user: data.user });
        
        // For new signups, ensure profileCompleted and hasCompletedProfile are explicitly false
        // This prevents issues with previously deleted accounts or cached data
        // Check both role and userType fields (backend might use either)
        const roleFromData = data.role || data.userType || (data.user && data.user.role) || (data.user && data.user.userType);
        const normalizedRole = normalizeRole(roleFromData);
        console.log('🔐 AuthContext - Role from signup response:', { roleFromData, normalizedRole });
        
        const signupData = {
          ...data,
          role: normalizedRole, // Ensure role is set correctly
          profileCompleted: false,
          hasCompletedProfile: false,
          isNewSignup: true // Flag to indicate this is a new signup
        };
        
        // Store user data first (without navigation)
        // Only call loginWithUserData for roles that need it (jobseekers and interns)
        // For other roles, we'll navigate directly without calling loginWithUserData
        if (normalizedRole === 'intern' || normalizedRole === 'jobSeeker') {
          // For interns and job seekers, store user data and go to setup-choice page
          loginWithUserData(signupData, false);
          console.log('🔐 AuthContext - New signup for intern/jobSeeker, redirecting to setup-choice');
          navigate('/setup-choice');
        } else if (normalizedRole === 'recruiter') {
          loginWithUserData(signupData, false);
          console.log('🔐 AuthContext - New signup for recruiter, redirecting to registration');
          navigate('/recruiter-registration');
        } else if (normalizedRole === 'employer') {
          loginWithUserData(signupData, false);
          console.log('🔐 AuthContext - New signup for employer, redirecting to registration');
          navigate('/employer-registration');
        } else if (normalizedRole === 'university') {
          loginWithUserData(signupData, false);
          console.log('🔐 AuthContext - New signup for university, redirecting to registration');
          navigate('/university-registration');
        } else if (normalizedRole === 'institute') {
          loginWithUserData(signupData, false);
          console.log('🔐 AuthContext - New signup for institute, redirecting to registration');
          navigate('/institute-registration');
        } else if (normalizedRole === 'association') {
          loginWithUserData(signupData, false);
          console.log('🔐 AuthContext - New signup for association, redirecting to association-registration');
          navigate('/association-registration');
        } else if (normalizedRole === 'ngo') {
          loginWithUserData(signupData, false);
          console.log('🔐 AuthContext - New signup for NGO, redirecting to registration');
          navigate('/ngo-registration');
        } else if (normalizedRole === 'admin') {
          loginWithUserData(signupData, false);
          console.log('🔐 AuthContext - New signup for admin, redirecting to admin dashboard');
          navigate('/admin');
        } else {
          // Fallback: use normal routing logic but log warning
          console.warn('🔐 AuthContext - New signup for unknown role:', normalizedRole, 'Raw role:', roleFromData);
          console.warn('🔐 AuthContext - Full signup data:', signupData);
          // Don't navigate to setup-choice for unknown roles - let loginWithUserData handle it
          loginWithUserData(signupData, true);
        }
      } else {
        // If no token returned, redirect to login
        console.log('🔐 AuthContext - No token in signup response, redirecting to login');
        navigate('/login');
      }
      
      return data;
    } catch (error) {
      console.error('🔐 AuthContext - Signup error:', error);
      throw error;
    }
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const hasRole = (requiredRole) => {
    if (!user || !user.role) return false;
    
    const userRole = normalizeRole(user.role);
    
    // Handle array of required roles
    if (Array.isArray(requiredRole)) {
      return requiredRole.some(role => normalizeRole(role) === userRole);
    }
    
    const required = normalizeRole(requiredRole);
    return userRole === required;
  };

  const isJobSeeker = () => hasRole('jobSeeker');
  const isRecruiter = () => hasRole('recruiter');
  const isEmployer = () => hasRole('employer');
  const isIntern = () => hasRole('intern');
  const isAdmin = () => hasRole('admin');

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    loginWithUserData,
    signup,
    logout,
    forceLogout,
    updateUser,
    hasRole,
    isJobSeeker,
    isRecruiter,
    isEmployer,
    isIntern,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
